// @ts-nocheck
/**
 * TemplateLoader.ts — WP-011 Template Loader
 *
 * Single entry point for all runtime template loading flows.
 *
 * Steps:
 *   1. Detect kind (template | bundle) from the top-level `kind` field.
 *   2. For bundles: extract the primary template from templates[0].
 *   3. Run structural validation. Structural failures always return loaded=null
 *      regardless of allowPartialLoad (structural validity is a hard requirement
 *      for producing a LoadedTemplate).
 *   4. Run semantic validation (unless skipSemanticValidation is true).
 *      Semantic failures return loaded=null unless allowPartialLoad=true.
 *   5. Normalize the document.
 *   6. Return a LoadResult with the merged ValidationResult.
 *
 * Never throws. All edge cases produce a diagnostic result.
 *
 * Pure TypeScript — no framework dependencies.
 */

import { makeFailResult, mergeResults } from '../../diagnostics';
import type { TemplateAssetManifest, TemplateBundle, TemplateDocument } from '../../types/canonical';
import { validateSemantics } from '../../validator/semantic';
import { validateStructure } from '../../validator/structural';
import { normalizeDocument } from './normalizer';
import type { LoadOptions, LoadResult } from './types';

// ── Internal helpers ──────────────────────────────────────────────────────────

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// ── Bundle path ───────────────────────────────────────────────────────────────

function loadBundle(
  raw: Record<string, unknown>,
  allowPartialLoad: boolean,
  skipSemanticValidation: boolean,
): LoadResult {
  if (raw.version !== 2) {
    return {
      loaded: null,
      validationResult: makeFailResult(
        'schema.invalid_version',
        `Unsupported bundle version '${String(raw.version)}'; expected 2`,
        { path: 'version' },
      ),
    };
  }

  if (!Array.isArray(raw.templates) || raw.templates.length === 0) {
    return {
      loaded: null,
      validationResult: makeFailResult(
        'schema.missing_required_field',
        'Bundle must contain at least one template in "templates"',
        { path: 'templates' },
      ),
    };
  }

  const firstTemplate: unknown = raw.templates[0];

  // Structural validation on the primary template — always gates loading
  const structResult = validateStructure(firstTemplate);
  if (!structResult.valid) {
    return { loaded: null, validationResult: structResult };
  }

  // Semantic validation
  let combinedResult = structResult;
  if (!skipSemanticValidation) {
    // validateSemantics internally re-runs structural as a guard and returns
    // only semantic issues when structural passes, so no double-counting here.
    const semResult = validateSemantics(firstTemplate);
    combinedResult = mergeResults(structResult, semResult);
  }

  if (!combinedResult.valid && !allowPartialLoad) {
    return { loaded: null, validationResult: combinedResult };
  }

  const document = normalizeDocument(firstTemplate as TemplateDocument);
  const bundle = raw as unknown as TemplateBundle;
  const assetManifest = isPlainObject(raw.assetManifest)
    ? (raw.assetManifest as unknown as TemplateAssetManifest)
    : undefined;

  return {
    loaded: {
      document,
      bundle,
      assetManifest,
      sourceMetadata: {
        source: 'bundle',
        importedAt: new Date().toISOString(),
      },
    },
    validationResult: combinedResult,
  };
}

// ── Template path ─────────────────────────────────────────────────────────────

function loadSingleTemplate(
  input: unknown,
  allowPartialLoad: boolean,
  skipSemanticValidation: boolean,
): LoadResult {
  // Structural validation — always gates producing a LoadedTemplate
  const structResult = validateStructure(input);
  if (!structResult.valid) {
    return { loaded: null, validationResult: structResult };
  }

  // Semantic validation
  let combinedResult = structResult;
  if (!skipSemanticValidation) {
    const semResult = validateSemantics(input);
    combinedResult = mergeResults(structResult, semResult);
  }

  if (!combinedResult.valid && !allowPartialLoad) {
    return { loaded: null, validationResult: combinedResult };
  }

  const document = normalizeDocument(input as TemplateDocument);

  return {
    loaded: {
      document,
      sourceMetadata: {
        source: 'singleDocument',
        importedAt: new Date().toISOString(),
      },
    },
    validationResult: combinedResult,
  };
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Loads and validates a template document or bundle from raw unknown input.
 * Returns a LoadResult. Never throws.
 */
export function loadTemplate(input: unknown, options?: LoadOptions): LoadResult {
  const allowPartialLoad = options?.allowPartialLoad ?? false;
  const skipSemanticValidation = options?.skipSemanticValidation ?? false;

  try {
    if (!isPlainObject(input)) {
      return {
        loaded: null,
        validationResult: makeFailResult(
          'schema.invalid_root',
          'Template root must be a plain object',
          { path: '' },
        ),
      };
    }

    const raw = input as Record<string, unknown>;

    if (raw.kind === 'bundle') {
      return loadBundle(raw, allowPartialLoad, skipSemanticValidation);
    }

    // Pass all other kinds (including 'template' and unknown) through the
    // structural validator, which will emit the appropriate error for unknown kinds.
    return loadSingleTemplate(input, allowPartialLoad, skipSemanticValidation);
  } catch {
    return {
      loaded: null,
      validationResult: makeFailResult(
        'schema.invalid_root',
        'Unexpected error during template loading',
      ),
    };
  }
}
